import { useEffect, useState } from 'react';

/** One word's timing in the rabbi recording, in seconds. */
export interface WordTiming {
  start: number;
  end: number;
}

const cache = new Map<string, WordTiming[]>();

/**
 * Normalizes the rabbi alignment JSON (same files PracticePage uses:
 * [{ text, start, end }, ...], numbers sometimes as strings or missing)
 * into ordered, non-overlapping timings.
 */
export function parseAlignment(raw: unknown): WordTiming[] {
  if (!Array.isArray(raw)) return [];
  const toNum = (v: unknown): number => {
    const n = typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : NaN;
    return Number.isFinite(n) ? n : NaN;
  };
  const words = raw.map((w) => {
    const o = (w && typeof w === 'object' ? w : {}) as Record<string, unknown>;
    return { start: toNum(o.start), end: toNum(o.end) };
  });

  let lastEnd = 0;
  return words.map((w) => {
    const start = Number.isNaN(w.start) ? lastEnd : Math.max(w.start, lastEnd);
    const end = Number.isNaN(w.end) || w.end <= start ? start + 0.3 : w.end;
    lastEnd = end;
    return { start, end };
  });
}

/** Index of the word being read at time t, or -1 before the first word. */
export function activeWordIndex(timings: WordTiming[], t: number): number {
  if (timings.length === 0 || t < timings[0].start) return -1;
  for (let i = 0; i < timings.length; i += 1) {
    if (t < timings[i].end) return i;
  }
  return timings.length - 1;
}

/**
 * Loads the alignment for a rabbi recording. Returns null while loading or when
 * it can't be loaded (missing file, or CORS on the audio bucket) — callers then
 * just skip word highlighting.
 */
export function useAlignment(url: string): WordTiming[] | null {
  const [timings, setTimings] = useState<WordTiming[] | null>(() => cache.get(url) ?? null);

  useEffect(() => {
    if (!url) {
      setTimings(null);
      return undefined;
    }
    const cached = cache.get(url);
    if (cached) {
      setTimings(cached);
      return undefined;
    }
    let cancelled = false;
    setTimings(null);
    (async (): Promise<void> => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const parsed = parseAlignment(await res.json());
        cache.set(url, parsed);
        if (!cancelled) setTimings(parsed);
      } catch (err) {
        console.error('[school] Alignment unavailable', url, err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  return timings;
}
